import type { RioTableRow, SortConfig, SortValue, TableColumn, TableObjectKey } from './types';

const isMissing = (value: SortValue): boolean =>
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value)) ||
    (value instanceof Date && Number.isNaN(value.getTime()));

/** Default comparator: handles Date, number, and locale-aware/numeric string comparison. */
export const compareSortValues = (a: SortValue, b: SortValue): number => {
    if (a instanceof Date || b instanceof Date) {
        return new Date(a ?? '').getTime() - new Date(b ?? '').getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        sensitivity: 'base',
        numeric: true,
    });
};

/** Toggles direction when the same key is clicked again, otherwise starts ascending. */
export const getNextSortConfig = <K extends string>(prev: SortConfig<K> | null, key: K): SortConfig<K> => {
    if (prev?.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    }

    return { key, dir: 'asc' };
};

const resolveSortValue = <T extends object>(record: T, column: TableColumn<T>): SortValue => {
    if (column.sortValue) {
        return column.sortValue(record);
    }

    const field = column.dataIndex ?? (column.key as TableObjectKey<T>);

    return (record as unknown as RioTableRow)[field] as SortValue;
};

/**
 * Returns a new sorted array based on the active sort config and the matching column.
 * Resolution order per column: `sorter` (full override) → `sortValue` accessor → field value.
 * Missing values are always sorted last, regardless of direction.
 */
export const sortData = <T extends object>(
    dataSource: readonly T[],
    sortConfig: SortConfig<string> | null | undefined,
    columns: readonly TableColumn<T>[]
): T[] => {
    if (!sortConfig) {
        return [...dataSource];
    }

    const column = columns.find(col => String(col.key) === sortConfig.key);
    if (!column || column.sortable !== true) {
        return [...dataSource];
    }

    const direction = sortConfig.dir === 'asc' ? 1 : -1;

    return [...dataSource].sort((a, b) => {
        const aValue = resolveSortValue(a, column);
        const bValue = resolveSortValue(b, column);

        const aMissing = isMissing(aValue);
        const bMissing = isMissing(bValue);

        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;

        if (column.sorter) {
            return column.sorter(a, b) * direction;
        }

        return compareSortValues(aValue, bValue) * direction;
    });
};
